<template>
    <div class="overlay" @click.self="$emit('close')">
        <div class="panel">
            <div class="panel-header">
                <span>[ export cv ]</span>
                <button class="close-btn" @click="$emit('close')">[x]</button>
            </div>

            <div class="panel-body">
                <!-- Presets -->
                <div class="row">
                    <span class="label">presets:</span>
                    <button class="btn" @click="applyPreset('full')">[ full cv ]</button>
                    <button class="btn" @click="applyPreset('technical')">
                        [ technical ]
                    </button>
                    <button class="btn" @click="applyPreset('recent')">
                        [ recent only ]
                    </button>
                </div>

                <!-- Sections -->
                <div class="section-title">sections:</div>

                <div class="check-row">
                    <label>
                        <input type="checkbox" v-model="sel.cv" />
                        curriculum vitae
                    </label>
                </div>

                <div class="check-row">
                    <label>
                        <input
                            type="checkbox"
                            v-model="sel.about"
                            @change="onAboutChange"
                        />
                        about
                    </label>
                    <div class="sub-checks" v-if="sel.about">
                        <label>
                            <input type="checkbox" v-model="sel.aboutContacts" />
                            contacts
                        </label>
                        <label>
                            <input type="checkbox" v-model="sel.aboutWhoIAm" />
                            who i am
                        </label>
                        <label>
                            <input type="checkbox" v-model="sel.aboutEducation" />
                            education
                        </label>
                        <label>
                            <input type="checkbox" v-model="sel.aboutSkills" />
                            skills
                        </label>
                    </div>
                </div>

                <div class="check-row">
                    <label>
                        <input type="checkbox" v-model="sel.opensource" />
                        open source
                    </label>
                </div>

                <div class="check-row">
                    <label>
                        <input type="checkbox" v-model="sel.blog" />
                        blog posts
                    </label>
                </div>

                <div class="check-row">
                    <label>
                        <input
                            type="checkbox"
                            v-model="sel.projects"
                            @change="onProjectsChange"
                        />
                        projects
                    </label>
                    <div class="sub-checks" v-if="sel.projects">
                        <label>
                            <input type="checkbox" v-model="sel.projects2020" />
                            2020+
                        </label>
                        <label>
                            <input type="checkbox" v-model="sel.projects2015" />
                            2015–2019
                        </label>
                        <label>
                            <input type="checkbox" v-model="sel.projects2010" />
                            2010–2014
                        </label>
                        <label>
                            <input type="checkbox" v-model="sel.projects2000" />
                            2000–2009
                        </label>
                    </div>
                </div>

                <!-- Tech filter -->
                <div class="section-title">
                    filter by tech (applies to projects &amp; open source):
                </div>
                <input
                    class="tech-input"
                    type="text"
                    v-model="techFilter"
                    placeholder="e.g. rust, react, aws"
                />

                <!-- Style -->
                <div class="section-title">pdf style:</div>
                <div class="row">
                    <button
                        class="btn"
                        :class="{ active: pdfStyle === 'professional' }"
                        @click="pdfStyle = 'professional'"
                    >
                        [ professional ]
                    </button>
                    <button
                        class="btn"
                        :class="{ active: pdfStyle === 'compact' }"
                        @click="pdfStyle = 'compact'"
                    >
                        [ compact ]
                    </button>
                    <button
                        class="btn"
                        :class="{ active: pdfStyle === 'classic' }"
                        @click="pdfStyle = 'classic'"
                    >
                        [ classic ]
                    </button>
                </div>
                <div v-if="pdfStyle === 'classic'" class="style-note">
                    classic: formal layout with photo, matches the official cv
                </div>

                <!-- Export buttons -->
                <div class="export-row">
                    <button class="btn export-btn" :disabled="loading" @click="exportPdf">
                        {{ loading ? '[ generating... ]' : '[ export pdf ]' }}
                    </button>
                    <button
                        class="btn export-btn"
                        :disabled="loading"
                        @click="exportMarkdown"
                    >
                        [ export markdown ]
                    </button>
                </div>

                <div v-if="error" class="error">{{ error }}</div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

function loadProfilePhoto(): Promise<string | null> {
    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            const canvas = document.createElement('canvas');
            const maxW = 400;
            const scale = Math.min(1, maxW / img.naturalWidth);
            canvas.width = img.naturalWidth * scale;
            canvas.height = img.naturalHeight * scale;
            canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL('image/jpeg', 0.85));
        };
        img.onerror = () => resolve(null);
        img.src = '/profile.jpg';
    });
}
import {
    ContentItem,
    ExportSelection,
    PdfStyle,
    DEFAULT_SELECTION,
    filterContent,
    toPdfmakeDefinition,
    toMarkdown,
} from '../utils/exportContent';

export default defineComponent({
    name: 'ExportPanel',
    emits: ['close'],
    props: {
        content: {
            type: Array as PropType<ContentItem[]>,
            required: true,
        },
    },
    data() {
        return {
            sel: { ...DEFAULT_SELECTION } as ExportSelection,
            techFilter: '' as string,
            pdfStyle: 'professional' as PdfStyle,
            loading: false,
            error: '' as string,
        };
    },
    computed: {
        techKeywords(): string[] {
            return this.techFilter
                .split(',')
                .map((k) => k.trim())
                .filter(Boolean);
        },
    },
    methods: {
        applyPreset(preset: 'full' | 'technical' | 'recent') {
            const base: ExportSelection = {
                cv: false,
                about: true,
                aboutContacts: true,
                aboutWhoIAm: false,
                aboutEducation: false,
                aboutSkills: true,
                opensource: false,
                blog: false,
                projects: true,
                projects2020: true,
                projects2015: false,
                projects2010: false,
                projects2000: false,
            };

            if (preset === 'full') {
                this.sel = { ...DEFAULT_SELECTION };
                this.techFilter = '';
            } else if (preset === 'technical') {
                this.sel = {
                    ...base,
                    aboutWhoIAm: false,
                    aboutEducation: false,
                    opensource: true,
                    projects2015: true,
                };
                this.techFilter = '';
            } else if (preset === 'recent') {
                this.sel = { ...base, aboutWhoIAm: true };
                this.techFilter = '';
            }
        },

        onAboutChange() {
            if (!this.sel.about) {
                this.sel.aboutContacts = false;
                this.sel.aboutWhoIAm = false;
                this.sel.aboutEducation = false;
                this.sel.aboutSkills = false;
            } else {
                this.sel.aboutContacts = true;
                this.sel.aboutWhoIAm = true;
                this.sel.aboutEducation = true;
                this.sel.aboutSkills = true;
            }
        },

        onProjectsChange() {
            if (!this.sel.projects) {
                this.sel.projects2020 = false;
                this.sel.projects2015 = false;
                this.sel.projects2010 = false;
                this.sel.projects2000 = false;
            } else {
                this.sel.projects2020 = true;
                this.sel.projects2015 = true;
                this.sel.projects2010 = true;
                this.sel.projects2000 = true;
            }
        },

        async exportPdf() {
            this.loading = true;
            this.error = '';
            try {
                // Lazy-load pdfmake only when needed to keep initial bundle small
                const [pdfMakeModule, pdfFontsModule] = await Promise.all([
                    import('pdfmake/build/pdfmake'),
                    import('pdfmake/build/vfs_fonts'),
                ]);
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const pdfMake = (pdfMakeModule as any).default ?? pdfMakeModule;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const pdfFonts = (pdfFontsModule as any).default ?? pdfFontsModule;

                pdfMake.addVirtualFileSystem(pdfFonts);
                pdfMake.addFonts({
                    Roboto: {
                        normal: 'Roboto-Regular.ttf',
                        bold: 'Roboto-Medium.ttf',
                        italics: 'Roboto-Italic.ttf',
                        bolditalics: 'Roboto-MediumItalic.ttf',
                    },
                });

                const filtered = filterContent(this.content, this.sel, this.techKeywords);
                const photoDataUrl =
                    this.pdfStyle === 'classic'
                        ? (await loadProfilePhoto()) ?? undefined
                        : undefined;
                const docDef = toPdfmakeDefinition(filtered, this.pdfStyle, photoDataUrl);
                await pdfMake.createPdf(docDef).download('roberto-previdi-cv.pdf');
            } catch (e) {
                this.error = String(e);
            } finally {
                this.loading = false;
            }
        },

        exportMarkdown() {
            const filtered = filterContent(this.content, this.sel, this.techKeywords);
            const md = `# Roberto Previdi — CV\n\n${toMarkdown(filtered)}`;
            const blob = new Blob([md], { type: 'text/markdown' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'roberto-previdi-cv.md';
            a.click();
            URL.revokeObjectURL(url);
        },
    },
});
</script>

<style scoped>
.overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.75);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding-top: 60px;
    z-index: 1000;
}

.panel {
    background: #0a0a0a;
    border: 1px solid green;
    color: green;
    font-family: monospace;
    font-size: 14px;
    width: 520px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
}

.panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    border-bottom: 1px solid green;
}

.panel-body {
    padding: 12px 14px;
    overflow-y: auto;
    flex: 1;
}

.section-title {
    color: #4caf50;
    margin: 12px 0 6px;
    font-size: 12px;
    letter-spacing: 0.05em;
}

.check-row {
    margin-bottom: 4px;
}

.check-row label {
    cursor: pointer;
}

.sub-checks {
    margin-left: 18px;
    display: flex;
    flex-wrap: wrap;
    gap: 6px 16px;
    margin-top: 3px;
    margin-bottom: 4px;
}

.sub-checks label {
    cursor: pointer;
    font-size: 13px;
    color: #99cc99;
}

input[type='checkbox'] {
    accent-color: green;
    margin-right: 4px;
}

.row {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 4px;
}

.label {
    color: #4caf50;
    font-size: 12px;
}

.style-note {
    font-size: 11px;
    color: #4a8c4a;
    margin-top: 2px;
    margin-bottom: 4px;
}

.tech-input {
    background: #000;
    border: 1px solid green;
    color: green;
    font-family: monospace;
    font-size: 13px;
    padding: 4px 8px;
    width: 100%;
    box-sizing: border-box;
    margin-bottom: 4px;
}

.tech-input::placeholder {
    color: #2a5c2a;
}

.tech-input:focus {
    outline: none;
    border-color: #4caf50;
}

.btn {
    background: none;
    border: none;
    color: green;
    font-family: monospace;
    font-size: 13px;
    cursor: pointer;
    padding: 0;
}

.btn:hover {
    color: #4caf50;
}

.btn.active {
    color: #fff;
    text-decoration: underline;
}

.btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.close-btn {
    background: none;
    border: none;
    color: green;
    font-family: monospace;
    font-size: 14px;
    cursor: pointer;
}

.export-row {
    display: flex;
    gap: 16px;
    margin-top: 16px;
    border-top: 1px solid #1a3a1a;
    padding-top: 12px;
}

.export-btn {
    font-size: 14px;
    color: #4caf50;
}

.export-btn:hover:not(:disabled) {
    color: #fff;
}

.error {
    color: #cc4444;
    font-size: 12px;
    margin-top: 8px;
}
</style>
